import { z } from "zod";

import { ENTERPRISE_TENANT_TYPES } from "./tenant";

/**
 * KANONİK DAVET MOTORU — paylaşılan sözlük (CLOS-1120 · S5-V4 FINAL V4).
 *
 * ### ⚠ Tek `purpose` sözlüğü, dört adapter
 *
 * Raporun *"KANONİK DAVET MOTORU — TEK SOURCE OF TRUTH"* bölümü dört amaç sayıyor ve
 * hepsinin aynı yaşam döngüsünü (`PENDING -> ACCEPTED | REJECTED | EXPIRED | CANCELLED`)
 * paylaşmasını istiyor. Repo envanteri (`docs/S5-V4-FAZ0-ENVANTER.md` §1) dört ayrı
 * tablo ve BEŞ kaynak buldu; Halit'in kararı (b): tablolar adapter olarak kalır, durum
 * sözlüğü ve Davetlerim projection'ı tek yerden gelir. `kind` adapter'ın anahtarı
 * (hangi tablo), `purpose` raporun kanonik amacı — ikisi bire bir değil: müvekkil ilişkisi
 * hem temsilci davetinden hem hukuk bürosunun e-postaya gönderdiği hizmet talebinden
 * doğabiliyor.
 *
 * ### ⚠ Mevcut adlar YENİDEN KULLANILIYOR (kartın kuralı)
 *
 * `organization | contract_participant | representative` Davetlerim merkezinin (CLOS-227)
 * yerleşik anahtarları; iki yeni anahtar eklendi, hiçbiri yeniden adlandırılmadı — istemci
 * ve testler aynı sözlüğü okumaya devam ediyor.
 */
export const INVITATION_PURPOSES = [
  "organization_membership",
  "client_relationship",
  "contract_party",
  "contract_external_guest",
] as const;
export type InvitationPurpose = (typeof INVITATION_PURPOSES)[number];

export const INVITATION_INBOX_KINDS = [
  "organization",
  "contract_participant",
  "representative",
  "contract_org",
  "service_relationship",
] as const;
export type InvitationInboxKind = (typeof INVITATION_INBOX_KINDS)[number];

/** Adapter → kanonik amaç. Her anahtarın TEK amacı var; dispatcher bu haritadan başka yol bilmez. */
export const INVITATION_KIND_PURPOSE: Record<InvitationInboxKind, InvitationPurpose> = {
  organization: "organization_membership",
  contract_participant: "contract_external_guest",
  representative: "client_relationship",
  contract_org: "contract_party",
  service_relationship: "client_relationship",
};

/** Kanonik yaşam döngüsü: `PENDING -> ACCEPTED | REJECTED | EXPIRED | CANCELLED` (rapor). */
export const INVITATION_LIFECYCLE_STATUSES = [
  "pending",
  "accepted",
  "rejected",
  "cancelled",
  "expired",
] as const;
export type InvitationLifecycleStatus = (typeof INVITATION_LIFECYCLE_STATUSES)[number];

/**
 * Kullanıcıya gösterilen amaç etiketi (INV-AT-12: *"organizasyon, müvekkil, contract party
 * ve external guest invitation'larını doğru context label'ıyla ayırır"*). Sözlük burada:
 * API ve web aynı etiketi okur, iki yüzey bir gün ayrışmaz.
 */
export const INVITATION_PURPOSE_LABELS: Record<InvitationPurpose, string> = {
  organization_membership: "Organizasyon üyeliği",
  client_relationship: "Müvekkil ilişkisi",
  contract_party: "Sözleşme tarafı",
  contract_external_guest: "Haricî misafir erişimi",
};

/**
 * Kararın HANGİ BAĞLAMDA verileceği.
 *
 * - `null`: davet kişiye; kabul yalnız kişinin oturumuyla (üyelik, temsilci, misafir).
 * - `organization`: davet bir adrese gitti ama kabul bir ORGANİZASYON adına (K1 · sözleşme
 *   tarafı). Alıcı hangi organizasyon adına yanıtladığını seçer; sunucu o organizasyonda
 *   üyelik + yetki kapısını yeniden uygular.
 * - `client_area`: hukuk bürosunun hizmet daveti; kabul bir Müvekkil Alanı / kurumsal alan
 *   adına ve o alanın hizmet yetkisiyle.
 */
export type InvitationDecisionContext = "organization" | "client_area" | "self" | null;

/** Kabul/ret için seçilebilecek bağlam — sunucu hesaplar, istemci yalnız listeler. */
export type InvitationAcceptTarget = {
  tenantId: string;
  name: string;
  type: string;
};

export type InvitationInboxItem = {
  kind: InvitationInboxKind;
  purpose: InvitationPurpose;
  id: string;
  /** Daveti gönderen organizasyonun adı. */
  organizationName: string;
  /** Kanonik durum — kaynak akışların yazdığı değer. */
  status: string;
  /** Türetilmiş gösterim: `pending` + süresi geçmiş → `expired`. */
  displayStatus: string;
  expiresAt: string;
  createdAt: string;
  decisionContext: InvitationDecisionContext;
  /** Yalnız `decisionContext !== null` olan satırlarda: kullanıcının adına yanıt verebileceği alanlar. */
  acceptTargets?: InvitationAcceptTarget[];
  /**
   * KURUM OLUŞTUR VE KABUL ET (CLOS-1125 / Halit S2): kurum daveti, kullanıcının yetkili
   * kurumu yoksa ve taraf henüz bir organizasyona bağlı değilse tek adımda kurum kurup kabul
   * edilebilir. Tür kullanıcı seçer (varsayılan yok); ad davetteki taraf unvanıyla ÖN DOLU.
   */
  canCreateOrganization?: boolean;
  suggestedOrganizationName?: string | null;
  /**
   * HİZMET DAVETİNİN YÖNÜ (CLOS-1185) — yalnız `service_relationship` satırlarında.
   *
   * ⚠ İKİ YÖN AYNI TABLODA AMA AYNI UÇTAN KARARA BAĞLANMIYOR. Büro başlatımlı davete
   * müvekkil tarafı karar verir; sözleşmeden başlayan davete SAĞLAYICI karar verir ve o
   * yol hangi sağlayıcı olarak kabul edildiğini ayrıca çözer. Ekran doğru ucu seçebilsin
   * diye yön yükte taşınıyor; ekranın kendi tahmin etmesi iki akışı karıştırırdı.
   */
  serviceDirection?: "law_firm_offer" | "contract_origin_request";
  /** Yalnız organizasyon davetinde: kabulde verilecek rolün adı. */
  roleName?: string;
  /** Sözleşmeye bağlı davetlerde: hedef sözleşmenin başlığı. */
  contractTitle?: string;
  /** Yalnız temsilci davetinde: temsil edilecek müvekkilin görünen adı. */
  clientDisplayName?: string;
  /** Sözleşme erişimi veren davetlerde: verilecek erişim düzeyi. */
  accessLevel?: "read" | "write";
  /** Haricî misafir davetinde: kabul sonrası erişimin biteceği an (`null` = kaldırılana kadar). */
  accessEndsAt?: string | null;
  /** Yalnız hizmet davetinde: teklif edilen kapsam etiketi. */
  scopeLabel?: string;
};

export type InvitationInbox = { items: InvitationInboxItem[]; pendingCount: number };

/**
 * Davetlerim'den karar gövdesi. `tenantId` yalnız bağlam isteyen türlerde okunur; kişi
 * davetlerinde gönderilse de YOK SAYILIR — kişi daveti bir organizasyon adına kabul
 * edilemez.
 */
export const inboxCreateOrganizationSchema = z.object({
  name: z
    .string({ error: "Organizasyon adı zorunludur" })
    .trim()
    .min(2, "Organizasyon adı en az 2 karakter olmalıdır")
    .max(200, "Organizasyon adı en fazla 200 karakter olabilir"),
  /** Tür AÇIKÇA seçilir; varsayılan YOK (hukuki taraf hukuk bürosu da olabilir). */
  type: z.enum(ENTERPRISE_TENANT_TYPES, "Organizasyon türü seçilmelidir"),
});
export type InboxCreateOrganizationInput = z.infer<typeof inboxCreateOrganizationSchema>;

export const invitationInboxDecisionSchema = z
  .object({
    tenantId: z.uuid("Geçerli bir organizasyon kimliği gereklidir").optional(),
    /** Yalnız `contract_org` kabulünde ve `tenantId` YOKKEN (CLOS-1125 / Halit S2). */
    createOrganization: inboxCreateOrganizationSchema.optional(),
  })
  .refine((g) => !(g.tenantId !== undefined && g.createOrganization !== undefined), {
    message: "Mevcut organizasyon seçimi ile yeni organizasyon birlikte gönderilemez",
    path: ["createOrganization"],
  });
export type InvitationInboxDecisionInput = z.infer<typeof invitationInboxDecisionSchema>;
